// Crew roles and wage model (DESIGN §4).
// Wage/day = laborWageIndex(year) × roleMult × (0.8 + 0.6 × skill/100) × envMult.
import { M2_ROLES } from './m2/roles.js';
import { M3_ROLES, M3_ROLE_FROM_YEAR } from './m3/roles.js';
import { M4_ROLES } from './m4/roles.js';
import { m4Obj } from './m4/gate.js';

export const ROLES = {
  // Era 1 (Pioneer)
  driller:       { name: 'Driller',       roleMult: 1.8, era: 'pioneer', fromYear: 1859, desc: 'Runs the rig, feels the tools through the cable. No driller, no drilling.' },
  tool_dresser:  { name: 'Tool Dresser',  roleMult: 1.3, era: 'pioneer', fromYear: 1859, desc: 'Heats and hammers dull bits back to an edge at the forge, and fires the boiler between times.' },
  laborer:       { name: 'Laborer',       roleMult: 1.0, era: 'pioneer', fromYear: 1859, desc: 'Strong back for the spring pole, the bailer and the barrels.' },
  fireman:       { name: 'Fireman',       roleMult: 1.1, era: 'pioneer', fromYear: 1859, toYear: 1945, steamOnly: true, desc: 'Tends the boiler full time: keeps the glass topped up, watches the gauge and fires her evenly. Fewer explosions, a little less coal.' },
  teamster:      { name: 'Teamster',      roleMult: 1.0, era: 'pioneer', fromYear: 1859, desc: 'Hauls barrels down mud roads to the creek landing. Hates pipelines.' },
  geologist:     { name: 'Geologist',     roleMult: 3.0, era: 'pioneer', fromYear: 1885, desc: 'Reads anticlines instead of creek beds. Expensive, usually right.' },
  guard:         { name: 'Guard',         roleMult: 0.9, era: 'pioneer', fromYear: 1859, desc: 'A rifle, a lantern and a long night watching the tree line. Later eras: private security contractors.' },
  inventor:      { name: 'Engineer / Inventor', roleMult: 2.5, era: 'pioneer', fromYear: 1859, desc: 'Works the lab. Each one assigned cuts project time 8%; some are geniuses.' },
  // Era 2
  derrickman:    { name: 'Derrickman',    roleMult: 1.3, era: 'gusher', fromYear: 1901, desc: 'Works the monkey board racking pipe high in the derrick.' },
  roughneck:     { name: 'Roughneck',     roleMult: 1.0, era: 'gusher', fromYear: 1901, desc: 'Floor hand on the rotary: tongs, slips and grease.' },
  motorman:      { name: 'Motorman',      roleMult: 1.3, era: 'gusher', fromYear: 1901, desc: 'Keeps the engines turning, the boiler fed and the pumps pumping.' },
  toolpusher:    { name: 'Toolpusher',    roleMult: 2.8, era: 'gusher', fromYear: 1901, desc: 'Supervises up to three rigs and runs drilling programs.' },
  safety_officer: { name: 'Safety Officer', roleMult: 2.0, era: 'gusher', fromYear: 1910, desc: 'Walks the rigs with a checklist. -20% incident chance where assigned.' },
  mud_man:       { name: 'Mud Man',       roleMult: 1.6, era: 'gusher', fromYear: 1925, desc: 'Mixes clay and barite to hold the hole and the pressure.' },
  // Era 3-4
  mud_engineer:  { name: 'Mud Engineer',  roleMult: 2.5, era: 'postwar', fromYear: 1946, desc: 'Weighs, tests and tunes the mud system by the hour.' },
  petroleum_engineer: { name: 'Petroleum Engineer', roleMult: 3.2, era: 'postwar', fromYear: 1946, desc: 'Designs completions and squeezes more out of every reservoir.' },
  crane_op:      { name: 'Crane Operator', roleMult: 1.5, era: 'postwar', fromYear: 1947, offshore: true, desc: 'Lifts everything onto a rig that has no road.' },
  oim:           { name: 'Offshore Installation Manager', roleMult: 3.5, era: 'postwar', fromYear: 1947, offshore: true, desc: 'Captain of the platform, answerable for every soul aboard.' },
  medic:         { name: 'Medic',         roleMult: 2.0, era: 'postwar', fromYear: 1947, offshore: true, desc: 'The nearest hospital is a helicopter ride away. Halves injury duration (offshore only).' },
  cook:          { name: 'Cook',          roleMult: 1.0, era: 'postwar', fromYear: 1947, offshore: true, desc: 'Steak night keeps a crew happy for a fortnight. +10 morale.' },
  // Era 5
  directional_driller: { name: 'Directional Driller', roleMult: 4.0, era: 'shale', fromYear: 2000, desc: 'Steers the bit two miles sideways through a ten-foot seam.' },
  mwd_tech:      { name: 'MWD Technician', roleMult: 2.6, era: 'shale', fromYear: 2000, desc: 'Decodes mud-pulse telemetry from the bottom of the hole.' },
  // Era 6
  robotics_tech: { name: 'Robotics Technician', roleMult: 3.0, era: 'future', fromYear: 2030, desc: 'Keeps the pipe-handling robots and subsea drones alive.' },
  remote_ops:    { name: 'Remote Operator', roleMult: 2.8, era: 'future', fromYear: 2030, desc: 'Drills from a screen a thousand miles away.' },
  // Era 7
  astronaut_driller: { name: 'Astronaut-Driller', roleMult: 4.5, era: 'space', fromYear: 2060, space: true, desc: 'A roughneck with a pressure suit and a pilot license.' },
  cryo_specialist:   { name: 'Cryo Specialist',   roleMult: 4.0, era: 'space', fromYear: 2060, space: true, desc: 'Knows how steel behaves at minus 179 degrees.' },
  rad_medic:         { name: 'Rad-Medic',         roleMult: 3.5, era: 'space', fromYear: 2060, space: true, desc: 'Counts sieverts so the crew does not have to.' },
  // Era 2 additions (M2): src/data/m2/roles.js
  ...M2_ROLES,
  // Era 3-4 additions (M3): src/data/m3/roles.js
  ...M3_ROLES,
  // Era 5 additions (M4, fromYear >= 2000): src/data/m4/roles.js
  ...m4Obj(M4_ROLES),
};
// M3: MWD and directional crews arrive earlier than DESIGN's Era 5 (only ever moves fromYear earlier, never before 1946).
for (const [id, y] of Object.entries(M3_ROLE_FROM_YEAR)) if (ROLES[id]) ROLES[id].fromYear = Math.max(1946, Math.min(ROLES[id].fromYear, y));

export const WAGE_KEYFRAMES = [
  [1860, 1.25], [1900, 1.75], [1920, 4], [1940, 5], [1950, 12], [1970, 30],
  [1980, 80], [1990, 110], [2000, 160], [2010, 250], [2025, 320],
];

export const ENV_MULT = { land: 1, offshore: 1.5, space: 20 };

// Linear interpolation; clamped before 1860, frozen after 2025.
export function laborWageIndex(year) {
  const k = WAGE_KEYFRAMES;
  if (!(year > k[0][0])) return k[0][1];
  if (year >= k[k.length - 1][0]) return k[k.length - 1][1];
  for (let i = 1; i < k.length; i++) {
    if (year <= k[i][0]) {
      const [y0, v0] = k[i - 1];
      const [y1, v1] = k[i];
      return v0 + (v1 - v0) * (year - y0) / (y1 - y0);
    }
  }
  return k[k.length - 1][1];
}

// $/day in era dollars. env: 'land' (default) | 'offshore' | 'space'.
export function marketWage(role, year, skill = 50, env = 'land') {
  const r = ROLES[role];
  if (!r) throw new Error(`unknown role ${role}`);
  const s = Math.max(0, Math.min(100, skill));
  return laborWageIndex(year) * r.roleMult * (0.8 + 0.6 * s / 100) * (ENV_MULT[env] ?? 1);
}

export function rolesForYear(year) {
  return Object.entries(ROLES).filter(([, r]) => r.fromYear <= year).map(([id]) => id);
}
